import { Address } from '@/types/general';
import { isValidIBAN, electronicFormatIBAN, friendlyFormatIBAN } from 'ibantools';

const ibanFirstFour = /^[A-Z]{2}[0-9]{2}/;
const yapealIbanSpec = /^CH[0-9]{2}(83019|30334)([0-9A-Za-z]{12})$/;
const isNumericRegex = /^[\d]*$/;
const isrReferenceFormat = /^(.{2})(.{1,5})?(.{1,5})?(.{1,5})?(.{1,5})?(.{1,5})?(.+)?$/;

export function isStartOfYapealIban(iban: string): boolean {
  const elIban = electronicFormatIBAN(iban);
  return !!elIban && /^CH[0-9]{2}(83019|30334)/.test(elIban);
  // return /^CH[0-9]{2}(83019|30334)/.test(iban.replaceAll(' ', ''));
}

export function isStartOfIban(iban: string): boolean {
  const elIban = electronicFormatIBAN(iban);
  return !!elIban && ibanFirstFour.test(elIban);
}

export function isYapealIban(iban: string): boolean {
  const efIban = electronicFormatIBAN(iban) ?? '';
  return isValidIBAN(efIban) && yapealIbanSpec.test(efIban ?? '');
}

export function formatYapealIban(iban: string): string {
  const efIban = electronicFormatIBAN(iban) ?? '';
  return `${efIban.substring(0, 4)} ${efIban.substring(4, 9)} ${efIban.substring(9, 21)}`;
}

export function formatIban(number: string | undefined): string {
  if (!number) return '';
  const efIban = electronicFormatIBAN(number) ?? '';
  if (!isValidIBAN(efIban)) {
    // invalid IBAN but still try to do our best to format it correclty
    return friendlyFormatIBAN(efIban) || '';
  }
  if (isYapealIban(efIban)) {
    return formatYapealIban(efIban);
  } else {
    return friendlyFormatIBAN(efIban) || '';
  }
}

function removeLeadingZeros(number: string): string {
  return Number.parseInt(number).toString();
}

export function formatESRNumber(number: string | undefined): string {
  if (!number) return '';
  if (number.length === 9 && Number.parseInt(number) !== undefined && !Number.isNaN(Number.parseInt(number))) {
    return `${number.substring(0, 2)}-${removeLeadingZeros(number.substring(2, 8))}-${number.substring(8)}`;
  }
  return number ?? '';
}

export function formatPaymentReference(value: string | undefined, padLeft = false): string {
  if (!value || value === '') return '';

  let ref = electronicFormatIBAN(value) || '';
  if (!isNumericRegex.test(ref)) {
    // format groups of four
    return friendlyFormatIBAN(ref) || ref;
  }
  if (padLeft) {
    ref = ref.padStart(24, '0');
  }
  if (isrReferenceFormat.test(ref)) {
    const matches = ref.match(isrReferenceFormat);
    return matches ? matches.slice(1).join(' ').trim() : ref;
  }

  return ref;
}

export function formatCardNumber(value: string | undefined): string {
  if (!value) {
    return '';
  }
  let cardNb = value;
  if (value.length === 4) {
    cardNb = cardNb.padStart(16, '*');
  }
  // add space every 4 character
  return friendlyFormatIBAN(cardNb) ?? cardNb;
}

export function formatAddressSingleLine(address: Partial<Address> | undefined): string {
  if (!address) {
    return '';
  }
  const adr = [
    `${address.street ? address.street : ''} ${address.houseNo ? address.houseNo : ''}`.trim(),
    `${address.zip ? address.zip : ''} ${address.town ? address.town : ''}`.trim(),
    `${address.countryName ? address.countryName : ''}`.trim()
  ];

  return adr.filter((el) => el !== '').join(', ');
}
